import React from "react";

import { faLightbulb } from "@fortawesome/pro-solid-svg-icons";
import { useField, useFormikContext } from "formik";
import isArray from "lodash/isArray";

import { DataTour } from "components/DataTour";
import { Warning } from "components/ErrorMessage";
import { Column, Row } from "components/Layout";
import { CombinedFormikMissingFieldIndicator } from "components/MissingFieldsIndicator";
import { ReactSelect } from "components/SearchableSelector";

import { EMPTY_ARRAY } from "lib";

import { simpleOptionComparer } from "lib/compare";

import { Error } from "./Error";
import { Label } from "./Label";
import { SuggestedValueGetter, useSuggestedValue } from "./lib";
import { SelectFieldOption } from "./types";
import { handleSelectAndScrollFocus } from "./focus";
import { SlimSecondaryButton } from "components/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export const getNullEmptyValue = () => null;
export const getUndefinedEmptyValue = () => undefined;
export const getFirstOnlyOption = field => (value, index, array) =>
  array.length === 1 ? value : simpleOptionComparer(field)(value);

const getValueFromSelectedOption = (selectedOption: SelectFieldOption) => {
  if (Array.isArray(selectedOption)) {
    return selectedOption.map(o => o.value);
  }
  return selectedOption.value;
};
export const flattenOptions = (
  options?: SelectFieldOption[],
): SelectFieldOption[] =>
  options?.reduce((acc, option) => {
    if (isArray(option.options)) {
      option.options.forEach((innerOption: SelectFieldOption) =>
        acc.push(innerOption),
      );
    } else {
      acc.push(option);
    }
    return acc;
  }, []) || [];

interface SelectFieldProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Field and option comparison callback
  comparer?: (field: any) => (option: any) => boolean;
  menuPortalTarget?: HTMLElement;
  disabled?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Empty value can be null or other types
  getEmptyValue?: () => any;
  isClearable?: boolean;
  isOptionDisabled?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Label can be string or React node
  label?: any;
  maxMenuHeight?: number;
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Select value type varies
  onChangeExtra?: null | ((value: any) => void);
  scrollOnFocus?: boolean;
  options: SelectFieldOption[];
  tooltip?: string;
  required?: boolean;
  suggestedValueFieldName?: SuggestedValueGetter;
  showValueSuggestedValueNotAvailable?: boolean;
  isMulti?: boolean;
  components?: object;
  altTooltip?: string | null;
  altColor?: string | null;
  warningText?: string | null;
  placeholder?: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- React-select filterOption callback
  filterOption?: any;
  autoFocus?: boolean;
  missingMLAFieldsWarningEnabled?: boolean;
  missingNASFieldWarningEnabled?: boolean;
  overrideMissingFieldName?: string | null;
  modelIds?: string[];
  formatOptionLabel?: (option: SelectFieldOption) => React.ReactNode;
}

export const SelectField = ({
  comparer = simpleOptionComparer,
  menuPortalTarget = undefined,
  disabled = false,
  getEmptyValue = getNullEmptyValue,
  isClearable = false,
  isOptionDisabled = undefined,
  label = undefined,
  maxMenuHeight = undefined,
  name,
  onChangeExtra = null,
  scrollOnFocus = true,
  options,
  tooltip = undefined,
  required = false,
  suggestedValueFieldName = undefined,
  showValueSuggestedValueNotAvailable = false,
  isMulti = false,
  components = {},
  altTooltip = null,
  altColor = null,
  warningText = null,
  placeholder = "Select...",
  filterOption = undefined,
  autoFocus = false,
  missingMLAFieldsWarningEnabled = false,
  missingNASFieldWarningEnabled = false,
  overrideMissingFieldName = undefined,
  modelIds = EMPTY_ARRAY,
  formatOptionLabel = undefined,
}: SelectFieldProps) => {
  const [field, meta] = useField(name);
  const { setFieldValue, setFieldTouched } = useFormikContext();
  const suggestedValue = useSuggestedValue(
    suggestedValueFieldName,
    field.value,
  );
  const suggestedLabel =
    suggestedValue === undefined
      ? ""
      : options.find(o => o.value === suggestedValue)?.label;

  const onFocus = e => handleSelectAndScrollFocus(e, false, scrollOnFocus);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- React-select action type
  type OnChangeActionType = { action?: any };
  const onChange = (e, { action }: OnChangeActionType): void => {
    // useField onChange doesnt work with a select, so use the form.setFieldValue.
    const newValue =
      action === "clear" ? getEmptyValue() : getValueFromSelectedOption(e);
    setFieldTouched(name, true, false).then(() =>
      setFieldValue(name, newValue, true),
    );
    return onChangeExtra && onChangeExtra(newValue);
  };

  const handleClickUseSuggestedValue = () => {
    onChange({ value: suggestedValue }, {});
  };

  const flattenedOptions = flattenOptions(options);

  const comparerFunc = comparer(field);
  let selectedValue =
    flattenedOptions && field.value !== null
      ? flattenedOptions.filter(comparerFunc)
      : getEmptyValue();

  if (isMulti && Array.isArray(selectedValue)) {
    selectedValue =
      field.value?.map(v => flattenedOptions.find(o => o.value === v)) || [];
  }

  const error = meta.touched && meta.error;
  const hasLabel = !!label;
  // We need to put the menu in a portal when we need the menu options to be able to overflow outside of the parent.
  // Eg in a modal, we dont want to scroll the content of the modal when the menu is open.

  return (
    <Column fullWidth data-field-root>
      <DataTour dataTour={name}>
        <Row justifyBetween>
          <Label
            htmlFor={name}
            data-field-label
            required={hasLabel && required}
            error={!!error}
            tooltip={tooltip}
            altTooltip={altTooltip}
            altColor={altColor}
          >
            {label}
          </Label>
          <CombinedFormikMissingFieldIndicator
            missingMLAFieldsWarningEnabled={missingMLAFieldsWarningEnabled}
            missingNASFieldWarningEnabled={missingNASFieldWarningEnabled}
            name={name}
            overrideMissingFieldName={overrideMissingFieldName}
            modelIds={modelIds}
          />
        </Row>
        <ReactSelect
          // TODO: Modify the below so it works better as per the comment above.
          isClearable={isClearable}
          isOptionDisabled={isOptionDisabled}
          data-tour={name}
          maxMenuHeight={maxMenuHeight || 300}
          menuPlacement="auto"
          menuPosition="fixed"
          value={selectedValue}
          options={options}
          labelledBy={name}
          onChange={onChange}
          error={!!error}
          isDisabled={disabled}
          menuPortalTarget={menuPortalTarget}
          isMulti={isMulti}
          components={components}
          placeholder={placeholder}
          filterOption={filterOption}
          autoFocus={autoFocus}
          onFocus={onFocus}
          aria-label={label || name}
          formatOptionLabel={formatOptionLabel}
        />
        {suggestedLabel ? (
          <div className="py-2 text-right">
            <SlimSecondaryButton
              onClick={handleClickUseSuggestedValue}
              type="button"
              verticalMargin={2}
            >
              <FontAwesomeIcon icon={faLightbulb} />
              &nbsp; {suggestedLabel} suggested
            </SlimSecondaryButton>
          </div>
        ) : suggestedValue && showValueSuggestedValueNotAvailable ? (
          <Error>{suggestedValue} not currently available</Error>
        ) : null}
        {error && <Error>{error}</Error>}
        {warningText && <Warning>{warningText}</Warning>}
      </DataTour>
    </Column>
  );
};
